<template>
    <div class="h-full flex flex-col">
        <div class="shrink-0 flex items-center justify-between py-4 pr-5 pl-6 bg-gray-950/50 border-b border-gray-700">
            <span class="font-semibold text-white">
                {{ !editing ? 'Call Reports' : activeReport ? 'Update Report' : 'Add Report' }}
            </span>
            <UButton @click="editReport()" size="md" color="white">
                {{ editing ? 'Close' : activeReport ? 'Update Report' : 'Add Report' }}
            </UButton>
        </div>
        <div class="flex-1 p-4 pb-5 space-y-5 overflow-auto">
            <UForm v-if="editing" ref="form" @submit="saveChanges()" :state="report" :validate-on="['submit']" class="flex flex-col h-full space-y-6">
                <UFormGroup name="reason" label="Reason for Calling">
                    <UInputMenu 
                        v-model="report.call_reason" 
                        @update:modelValue="form.submit()" 
                        :options="groupedOptions.reasons"
                        by="id"
                        size="lg"
                        nullable
                    />
                </UFormGroup>
                <UFormGroup name="notes" label="Notes" :ui="{ wrapper: 'flex flex-col flex-1', container: 'flex-1' }">
                    <UTextarea 
                        v-model="report.notes" 
                        @blur="form.submit()" 
                        size="xl" 
                        :ui="{ wrapper: 'h-full', base: 'h-full' }"
                    />
                </UFormGroup>
                <UFormGroup v-if="auth.hasRole(UserRole.doctor, UserRole.medicalDirector)" name="consultation" label="Recommendation" :ui="{ wrapper: 'flex flex-col flex-1', container: 'flex-1' }">
                    <UTextarea 
                        v-model="report.consultation" 
                        @blur="form.submit()"
                        size="xl" 
                        :ui="{ wrapper: 'h-full', base: 'h-full' }"
                    />
                </UFormGroup>
                <UFormGroup name="diagnosis" label="Suspected Diagnosis">
                    <UInputMenu 
                        v-model="report.diagnosis" 
                        @update:modelValue="form.submit()" 
                        :options="groupedOptions.diagnoses"
                        :ui="{ leading: { padding: { lg: '!ps-14' } }}"
                        by="id"
                        size="lg"
                        nullable
                    >
                        <template #leading v-if="report.diagnosis?.metadata?.priority">
                            <BadgePriority :priority="report.diagnosis.metadata.priority" />
                        </template>
                        <template #option="{ option }">
                            <div class="w-full flex items-center gap-2">
                                <BadgePriority v-if="option?.metadata?.priority" :priority="option.metadata?.priority" />
                                <p class="truncate">{{ option.label }}</p>
                            </div>
                        </template>
                    </UInputMenu>
                </UFormGroup>
                <UFormGroup name="decision" label="Final Decision">
                    <UInputMenu 
                        v-model="report.call_decision" 
                        @update:modelValue="form.submit()" 
                        :options="groupedOptions.decisions"
                        by="id"
                        size="lg"
                        nullable
                    />
                </UFormGroup>
            </UForm>
            <div v-else-if="!call.reports?.length" class="h-full flex flex-col items-center justify-center gap-3">
                <UIcon name="i-ph-clipboard-text-duotone" class="size-6 text-gray-500" />
                <p class="text-xs text-gray-500 italic">No previous reports.</p>
            </div>
            <div v-else class="overflow-auto space-y-3">
                <CallReportCard
                    v-for="report in sortedReports" 
                    :key="report.id" 
                    :report="report"
                    :call-id="call.id"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Call } from '~/types/call'
import { OptionNamespace, type Option } from '~/types/option'
import type { CallReport } from '~/types/callReport'
import CallReportCard from '~/components/call/CallReportCard.vue'
import { UserRole } from '~/types/user';

const props = defineProps<{
    call: Call
}>()

const auth = useAuthStore()
const client = useSupabaseClient()

const form = ref()
const options = ref<Option[]>([])
const autosave = ref<boolean>(false)
const editing = ref<boolean>(false)
const saving = ref<boolean>(false)

function openEditor() {
    if (editing.value) return
    editing.value = true
    setTimeout(() => autosave.value = true, 500)
}

defineExpose({ openEditor })

const report = ref<CallReport>({} as CallReport)

const isEmpty = computed(() => {
    return !report.value.call_reason && !report.value.notes && !report.value.consultation && !report.value.diagnosis && !report.value.call_decision
})

const activeReport = computed(() => {
    return props.call.reports?.find((report) => report.user_id === auth.user?.id && report.call_id === props.call.id)
})

const sortedReports = computed(() => {
    return props.call.reports?.sort((a, b) => {
        return (new Date(b.created_at)).getTime() - (new Date(a.created_at)).getTime()
    })
})

const groupedOptions = computed(() => {
    const _options = options.value.map((option: Option) => {
        return { ...option, label: [option.label, option.description].filter(Boolean).join(' / ') }
    })

    return {
        reasons: _options.filter((option) => option.namespace === OptionNamespace.callReason),
        diagnoses: _options.filter((option) => option.namespace === OptionNamespace.diagnosis),
        decisions: _options.filter((option) => option.namespace === OptionNamespace.callDecision),
    }
})

onMounted(() => {
    fetchData()

    if (activeReport.value) {
        report.value = Object.assign({}, activeReport.value)
    }
})

async function fetchData() {
    const { data } = await client.from('options')
        .select('id, namespace, label, description, metadata')
        .eq('is_archived', false)
        .order('label')

    if (data) {
        options.value = data
    }
}

function editReport() {
    if (editing.value) {
        saveChanges()
        autosave.value = false
        editing.value = false
    }
    else {
        editing.value = true
        setTimeout(() => autosave.value = true, 500)
    }
}

async function saveChanges() {
    if (editing.value && autosave.value && !saving.value && !isEmpty.value) {
        saving.value = true

        const { data } = await client.from('call_reports')
            .upsert({
                id: report.value.id,
                call_id: props.call.id,
                customer_id: props.call.customer.id,
                user_id: auth.user?.id,
                call_reason_id: report.value.call_reason?.id,
                call_decision_id: report.value.call_decision?.id,
                diagnosis_id: report.value.diagnosis?.id,
                notes: report.value.notes,
                consultation: report.value.consultation,
                updated_at: new Date().toISOString()
            } as never)
            .select(`
                *,
                user:users (id, name, role),
                call:calls (id, number),
                call_reason:options!call_reports_call_reason_id_fkey (id, label, description),
                call_decision:options!call_reports_call_decision_id_fkey (id, label, description),
                diagnosis:options!call_reports_diagnosis_id_fkey (id, label, description, metadata)
            `)
            .returns<CallReport[]>()

        if (data?.length) {
            report.value = data[0]
            if (report.value.call_reason) report.value.call_reason.label = [report.value.call_reason.label, report.value.call_reason.description].filter(Boolean).join(' / ')
            if (report.value.diagnosis) report.value.diagnosis.label = [report.value.diagnosis.label, report.value.diagnosis.description].filter(Boolean).join(' / ')
            if (report.value.call_decision) report.value.call_decision.label = [report.value.call_decision.label, report.value.call_decision.description].filter(Boolean).join(' / ')
            
            if (activeReport.value) {
                activeReport.value.call_reason = report.value.call_reason
                activeReport.value.diagnosis = report.value.diagnosis
                activeReport.value.call_decision = report.value.call_decision
                activeReport.value.notes = report.value.notes
                activeReport.value.consultation = report.value.consultation
                activeReport.value.updated_at = report.value.updated_at
            }
            else {
                props.call.reports?.unshift(report.value)
            }
        }

        saving.value = false
    }
}
</script>